import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Trash2, Upload, BookOpen, Edit2, Headphones, Download, Link2 } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import Seo from '@/components/shared/Seo';
import { ieltsApi } from '@/lib/ieltsApi';
import { toast } from 'sonner';

const SECTIONS = ['listening','reading','writing','speaking'];
// All supported IELTS question types — legacy + real-IELTS reading types.
const QTYPES = [
  'multiple_choice_one_answer',
  'multiple_choice_multiple_answers',
  'true_false_not_given',
  'yes_no_not_given',
  'matching_headings',
  'matching_information',
  'matching_features',
  'matching_sentence_endings',
  'sentence_completion',
  'summary_completion_from_text',
  'summary_completion_from_list',
  'note_completion',
  'table_completion',
  'flow_chart_completion',
  'diagram_label_completion',
  'short_answer',
  // Legacy
  'mcq', 'matching', 'fill_blank', 'essay_prompt', 'speaking_prompt',
];

/**
 * Validate + auto-normalize a hosted audio URL for an IELTS listening set.
 * Returns { ok: true, url } on success (where `url` may have been rewritten),
 * or { ok: false, error } with a human-readable message.
 *
 * Accepts:
 *   - https://res.cloudinary.com/<cloud>/video/upload/.../*.mp3       (canonical)
 *   - https://res.cloudinary.com/<cloud>/raw/upload/.../*.mp3
 *   - https://<your-bucket>.s3.<region>.amazonaws.com/.../*.mp3       (S3)
 *   - https://drive.google.com/uc?export=download&id=...              (Google Drive direct, .mp3 in URL ok)
 *   - Empty string ""  → clears the link (allowed)
 *
 * Auto-normalizes:
 *   - "//res.cloudinary.com/…"   → "https://res.cloudinary.com/…"
 *   - "res.cloudinary.com/…"     → "https://res.cloudinary.com/…"
 *   - "cloudinary.com/…"         → "https://res.cloudinary.com/…"  (cloudinary share-host promoted to delivery host)
 *
 * Rejects:
 *   - Anything not https://
 *   - Cloudinary console/share-page URLs (these don't deliver the file)
 *   - URLs whose pathname doesn't end in .mp3
 */
const normalizeAudioUrl = (input) => {
  if (input == null) return { ok: true, url: '' };
  let url = String(input).trim();
  if (!url) return { ok: true, url: '' };

  // Protocol-relative → https
  if (url.startsWith('//')) url = 'https:' + url;
  // Bare hostnames → prepend https://res.cloudinary.com fix-up for Cloudinary
  if (!/^https?:\/\//i.test(url)) {
    if (/^res\.cloudinary\.com\//i.test(url)) url = 'https://' + url;
    else if (/^cloudinary\.com\//i.test(url)) url = 'https://res.' + url;
    else return {
      ok: false,
      error: 'Please paste the full direct MP3 URL starting with https:// and ending with .mp3',
    };
  }
  if (!/^https:\/\//i.test(url)) {
    return { ok: false, error: 'URL must start with https:// (use the HTTPS version of the file)' };
  }
  // Reject Cloudinary share / console / media-library URLs — these are page links, not file links
  if (/cloudinary\.com\/console\//i.test(url) || /cloudinary\.com\/[^/]+\/media_library\b/i.test(url)) {
    return {
      ok: false,
      error: 'This is a Cloudinary console/share-page URL — open the asset and copy the "Direct URL" (must end in .mp3).',
    };
  }
  // Path must end in .mp3 (ignore query string + anchor)
  const cleanPath = url.split('?')[0].split('#')[0];
  if (!/\.mp3$/i.test(cleanPath)) {
    return {
      ok: false,
      error: 'URL must be a direct audio file ending in .mp3 — got: …' + cleanPath.slice(-32),
    };
  }
  return { ok: true, url };
};

const emptyQuestion = {
  section: 'reading', module: 'academic', topic: '', subtopic: '', difficulty: 'medium',
  question_type: 'multiple_choice_one_answer',
  passage_id: '', passage_text: '', transcript_text: '', audio_url: '', image_url: '',
  instructions: '', question_text: '', option_a: '', option_b: '', option_c: '', option_d: '', option_e: '',
  correct_answer: 'a', explanation: '', estimated_time_seconds: 75, band_level: '',
  source_type: 'winhouse', status: 'published',
};

const emptyPassage = {
  section: 'reading',
  module: 'academic',
  passage_id: '',
  passage_title: '',
  passage_text: '',
  passage_part: 'part_1',
  topic: '',
  subtopic: '',
  difficulty: 'medium',
  source_type: 'winhouse',
  status: 'published',
  questions: [],
};

const emptyListeningSet = {
  section: 'listening',
  module: 'academic',
  listening_set_id: '',
  section_number: 'section_1',
  audio_url: '',
  transcript_text: '',
  image_url: '',
  topic: '',
  subtopic: '',
  difficulty: 'easy',
  instructions: '',
  source_type: 'winhouse',
  status: 'published',
  questions: [],
};

const LISTENING_QTYPES = [
  'form_completion', 'note_completion', 'table_completion',
  'flow_chart_completion', 'sentence_completion', 'summary_completion',
  'map_labeling', 'diagram_labeling',
  'multiple_choice_one_answer', 'multiple_choice_multiple_answers',
  'matching', 'short_answer',
];

const AdminIeltsPanel = ({ initialTab = 'passages' } = {}) => {
  const [tab, setTab] = useState(initialTab);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ section: '', module: '', difficulty: '' });
  const [students, setStudents] = useState([]);
  // Passages tab state
  const [passages, setPassages] = useState([]);
  const [passagesLoading, setPassagesLoading] = useState(false);
  const [passageFilters, setPassageFilters] = useState({ module: '', difficulty: '' });
  const [editingPassage, setEditingPassage] = useState(null);
  // Listening tab state
  const [listeningSets, setListeningSets] = useState([]);
  const [listeningLoading, setListeningLoading] = useState(false);
  const [listeningFilters, setListeningFilters] = useState({ module: '', section_number: '', difficulty: '' });
  const [editingListening, setEditingListening] = useState(null);
  // Writing tab state
  const [writingPrompts, setWritingPrompts] = useState([]);
  const [writingLoading, setWritingLoading] = useState(false);
  const [writingFilters, setWritingFilters] = useState({ module: '', task_number: '', task_type: '', difficulty: '' });
  const [editingWriting, setEditingWriting] = useState(null);
  const writingFileRef = useRef(null);
  // Speaking tab state
  const [speakingPrompts, setSpeakingPrompts] = useState([]);
  const [speakingLoading, setSpeakingLoading] = useState(false);
  const [speakingFilters, setSpeakingFilters] = useState({ speaking_part: '', topic: '', difficulty: '', status: '' });
  const [editingSpeaking, setEditingSpeaking] = useState(null);
  const speakingFileRef = useRef(null);
  const fileRef = useRef(null);
  const passageFileRef = useRef(null);
  const listeningFileRef = useRef(null);
  const audioUrlsFileRef = useRef(null);
  const [mobile, setMobile] = useState(false);

  // ---- Question CRUD --------------------------------------------------------
  const loadQuestions = async () => {
    setLoading(true);
    try {
      const qs = Object.entries(filters).filter(([_, v]) => v).map(([k, v]) => `${k}=${v}`).join('&');
      const r = await ieltsApi.adminList(qs ? `?${qs}` : '');
      setList(r.questions || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (tab === 'questions') loadQuestions();
    // eslint-disable-next-line
  }, [filters, tab]);

  // ---- Passages CRUD --------------------------------------------------------
  const loadPassages = async () => {
    setPassagesLoading(true);
    try {
      const qs = Object.entries(passageFilters).filter(([_, v]) => v).map(([k, v]) => `${k}=${v}`).join('&');
      const r = await ieltsApi.adminPassageList(qs ? `?${qs}` : '');
      setPassages(r.passages || []);
    } catch (e) { toast.error(e.message); }
    finally { setPassagesLoading(false); }
  };
  useEffect(() => {
    if (tab === 'passages') loadPassages();
    // eslint-disable-next-line
  }, [passageFilters, tab]);

  // ---- Listening sets CRUD ------------------------------------------------
  const loadListening = async () => {
    setListeningLoading(true);
    try {
      const qs = Object.entries(listeningFilters).filter(([_, v]) => v).map(([k, v]) => `${k}=${v}`).join('&');
      const r = await ieltsApi.adminListeningList(qs ? `?${qs}` : '');
      setListeningSets(r.listening_sets || []);
    } catch (e) { toast.error(e.message); }
    finally { setListeningLoading(false); }
  };
  useEffect(() => {
    if (tab === 'listening') loadListening();
    // eslint-disable-next-line
  }, [listeningFilters, tab]);

  const openListeningEditor = async (sid) => {
    if (!sid) return setEditingListening(emptyListeningSet);
    try {
      const r = await ieltsApi.adminListeningGet(sid);
      const s = r.listening_set;
      const questions = (r.questions || []).map((q) => ({
        question_number: q.question_number,
        question_type: q.question_type || 'note_completion',
        instructions: q.instructions || '',
        group_id: q.group_id || '',
        question_text: q.question_text || '',
        option_a: q.option_a || '', option_b: q.option_b || '',
        option_c: q.option_c || '', option_d: q.option_d || '',
        option_e: q.option_e || '',
        correct_answer: q.correct_answer || '',
        explanation: q.explanation || '',
        estimated_time_seconds: q.estimated_time_seconds || 45,
        band_level: q.band_level || '',
      }));
      setEditingListening({ ...emptyListeningSet, ...s, questions });
    } catch (e) { toast.error(e.message); }
  };

  const saveListening = async () => {
    if (!editingListening) return;
    const payload = { ...editingListening };
    payload.questions = (payload.questions || []).map((q) => ({
      ...q,
      question_number: Number(q.question_number) || 0,
      estimated_time_seconds: Number(q.estimated_time_seconds) || 45,
    }));
    try {
      if (listeningSets.find((s) => s.listening_set_id === payload.listening_set_id)) {
        await ieltsApi.adminListeningUpdate(payload.listening_set_id, payload);
      } else {
        await ieltsApi.adminListeningCreate(payload);
      }
      toast.success(`Listening set saved (${payload.questions.length} questions)`);
      setEditingListening(null);
      loadListening();
    } catch (e) { toast.error(e.message); }
  };

  const removeListening = async (sid) => {
    if (!window.confirm('Delete this listening set AND all linked questions?')) return;
    try { await ieltsApi.adminListeningDelete(sid); toast.success('Deleted'); loadListening(); }
    catch (e) { toast.error(e.message); }
  };

  const onUploadListening = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const items = Array.isArray(parsed) ? parsed : (parsed.listening_sets || parsed.sets);
      if (!Array.isArray(items)) throw new Error('Expected an array of listening sets or {listening_sets: [...]}');
      const r = await ieltsApi.adminListeningBulk(items);
      toast.success(`Saved ${r.saved} listening sets${r.errors?.length ? ` · ${r.errors.length} errors` : ''}`);
      if (r.errors?.length) console.warn('Listening upload errors', r.errors);
      loadListening();
    } catch (e) { toast.error(e.message); }
    finally { if (listeningFileRef.current) listeningFileRef.current.value = ''; }
  };

  const onUploadAudioUrls = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const items = Array.isArray(parsed) ? parsed : (parsed.items || parsed.audio_urls);
      if (!Array.isArray(items)) throw new Error('Expected [{listening_set_id, audio_url}, ...]');

      // Validate every URL up-front so we fail loudly before hitting the backend
      const cleaned = [];
      const badRows = [];
      for (const row of items) {
        const v = normalizeAudioUrl(row?.audio_url);
        if (!v.ok) {
          badRows.push({ listening_set_id: row?.listening_set_id, audio_url: row?.audio_url, error: v.error });
        } else {
          cleaned.push({ listening_set_id: row?.listening_set_id, audio_url: v.url });
        }
      }
      if (badRows.length) {
        console.warn('Invalid audio URLs (mapping not uploaded):', badRows);
        toast.error(`${badRows.length} row(s) have invalid URLs — fix them and retry. First issue: ${badRows[0].error}`);
        return;
      }

      const r = await ieltsApi.adminListeningBulkAudio(cleaned);
      const updated = (r.updated || []).length;
      const cleared = (r.cleared || []).length;
      const nf = (r.not_found || []).length;
      const summary = `Audio URLs · ${updated} updated · ${cleared} cleared${nf ? ` · ${nf} set IDs not found` : ''} · ${r.total_with_audio} total sets with audio`;
      toast.success(summary);
      if (nf) console.warn('Not found:', r.not_found);
      loadListening();
    } catch (e) { toast.error(e.message); }
    finally { if (audioUrlsFileRef.current) audioUrlsFileRef.current.value = ''; }
  };

  const exportTranscripts = async () => {
    try {
      const qs = Object.entries(listeningFilters).filter(([_, v]) => v).map(([k, v]) => `${k}=${v}`).join('&');
      const r = await ieltsApi.adminListeningExportTranscripts(qs ? `?${qs}` : '');
      // Trigger a JSON download so the user can feed it straight into a TTS pipeline
      const blob = new Blob([JSON.stringify(r.items, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `ielts_listening_transcripts_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.count} transcripts`);
    } catch (e) { toast.error(e.message); }
  };

  const quickSetAudioUrl = async (sid, currentUrl) => {
    // eslint-disable-next-line no-alert
    const raw = window.prompt(
      `Set audio_url for ${sid}\n\nPaste a direct .mp3 URL (e.g. https://res.cloudinary.com/.../*.mp3).\nLeave blank to clear and fall back to transcript-only mode.`,
      currentUrl || '',
    );
    if (raw === null) return;  // user cancelled
    const v = normalizeAudioUrl(raw);
    if (!v.ok) {
      toast.error(v.error);
      return;
    }
    try {
      const r = await ieltsApi.adminListeningBulkAudio([{ listening_set_id: sid, audio_url: v.url }]);
      if (r.not_found?.length) {
        toast.error(`Set ${sid} not found`);
      } else if ((r.cleared || []).includes(sid)) {
        toast.success(`Cleared audio URL on ${sid}`);
      } else {
        toast.success(v.url !== raw.trim() ? `Updated audio URL (auto-normalized → ${v.url})` : `Updated audio URL on ${sid}`);
      }
      loadListening();
    } catch (e) { toast.error(e.message); }
  };

  const updateListeningField = (k, v) => setEditingListening((s) => ({ ...s, [k]: v }));
  const updateLQField = (idx, k, v) => setEditingListening((s) => {
    const next = [...(s.questions || [])];
    next[idx] = { ...next[idx], [k]: v };
    return { ...s, questions: next };
  });
  const addListeningQuestionRow = () => setEditingListening((s) => ({
    ...s,
    questions: [...(s.questions || []), {
      question_number: (s.questions?.length || 0) + 1,
      question_type: 'note_completion',
      instructions: '',
      question_text: '',
      option_a: '', option_b: '', option_c: '', option_d: '', option_e: '',
      correct_answer: '',
      explanation: '',
      estimated_time_seconds: 45,
      band_level: '',
    }],
  }));
  const removeListeningQuestionRow = (idx) => setEditingListening((s) => ({
    ...s,
    questions: (s.questions || []).filter((_, i) => i !== idx),
  }));

  const loadStudents = async () => {
    try { const r = await ieltsApi.adminStudents(); setStudents(r.students || []); } catch (e) { toast.error(e.message); }
  };

  // ---- Writing prompt CRUD --------------------------------------------------
  const WRITING_TASK_TYPES = [
    { v: '', label: 'All types' },
    { v: 'line_graph', label: 'Line graph' },
    { v: 'bar_chart', label: 'Bar chart' },
    { v: 'pie_chart', label: 'Pie chart' },
    { v: 'table', label: 'Table' },
    { v: 'process', label: 'Process diagram' },
    { v: 'map', label: 'Map' },
    { v: 'mixed_visual', label: 'Mixed visual' },
    { v: 'formal_letter', label: 'Formal letter' },
    { v: 'semi_formal_letter', label: 'Semi-formal letter' },
    { v: 'informal_letter', label: 'Informal letter' },
    { v: 'opinion', label: 'Opinion essay' },
    { v: 'discussion', label: 'Discussion essay' },
    { v: 'problem_solution', label: 'Problem-solution' },
    { v: 'advantages_disadvantages', label: 'Advantages/disadvantages' },
    { v: 'double_question', label: 'Double-question' },
  ];
  const emptyWriting = {
    prompt_id: '', module: 'academic', task_number: 1, task_type: 'line_graph',
    task_title: '', chart_type: '',
    instructions: '', prompt_text: '', image_url: '', sample_answer: '',
    band_level: 'Band 6-7', difficulty: 'medium', source_type: 'winhouse', status: 'published',
  };
  const loadWritingPrompts = async () => {
    setWritingLoading(true);
    try {
      const qs = Object.entries(writingFilters).filter(([_, v]) => v !== '' && v !== null).map(([k, v]) => `${k}=${v}`).join('&');
      const r = await ieltsApi.adminWritingList(qs ? `?${qs}` : '');
      setWritingPrompts(r.prompts || []);
    } catch (e) { toast.error(e.message); }
    finally { setWritingLoading(false); }
  };
  useEffect(() => {
    if (tab === 'writing') loadWritingPrompts();
    // eslint-disable-next-line
  }, [writingFilters, tab]);

  const openWritingEditor = async (pid) => {
    if (!pid) return setEditingWriting(emptyWriting);
    try {
      const p = await ieltsApi.adminWritingGet(pid);
      setEditingWriting({ ...emptyWriting, ...p });
    } catch (e) { toast.error(e.message); }
  };
  const saveWriting = async () => {
    if (!editingWriting) return;
    const payload = { ...editingWriting, task_number: Number(editingWriting.task_number) };
    if (!payload.prompt_id) {
      toast.error('prompt_id is required');
      return;
    }
    try {
      if (writingPrompts.find((p) => p.prompt_id === payload.prompt_id)) {
        await ieltsApi.adminWritingUpdate(payload.prompt_id, payload);
      } else {
        await ieltsApi.adminWritingCreate(payload);
      }
      toast.success('Writing prompt saved');
      setEditingWriting(null);
      loadWritingPrompts();
    } catch (e) { toast.error(e.message); }
  };
  const removeWriting = async (pid) => {
    if (!window.confirm('Delete this writing prompt?')) return;
    try { await ieltsApi.adminWritingDelete(pid); toast.success('Deleted'); loadWritingPrompts(); }
    catch (e) { toast.error(e.message); }
  };
  const onUploadWriting = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const items = Array.isArray(parsed) ? parsed : parsed.prompts;
      if (!Array.isArray(items)) throw new Error('Expected an array of prompts or {prompts: [...]}');
      const r = await ieltsApi.adminWritingBulk(items);
      toast.success(`Saved ${r.saved} prompts${r.errors?.length ? ` · ${r.errors.length} errors` : ''}`);
      if (r.errors?.length) console.warn('Writing upload errors', r.errors);
      loadWritingPrompts();
    } catch (e) { toast.error(e.message); }
    finally { if (writingFileRef.current) writingFileRef.current.value = ''; }
  };

  // ---- Speaking prompt CRUD -------------------------------------------------
  const emptySpeaking = {
    prompt_id: '', module: 'shared', speaking_part: 'part_1',
    topic: '', subtopic: '', cue_card_text: '', prompt_text: '',
    follow_up_questions: [], difficulty: 'medium', band_level: 'Band 6-7',
    source_type: 'winhouse', status: 'published',
  };
  const loadSpeakingPrompts = async () => {
    setSpeakingLoading(true);
    try {
      const qs = Object.entries(speakingFilters).filter(([_, v]) => v !== '' && v !== null).map(([k, v]) => `${k}=${v}`).join('&');
      const r = await ieltsApi.adminSpeakingList(qs ? `?${qs}` : '');
      setSpeakingPrompts(r.prompts || []);
    } catch (e) { toast.error(e.message); }
    finally { setSpeakingLoading(false); }
  };
  useEffect(() => {
    if (tab === 'speaking') loadSpeakingPrompts();
    // eslint-disable-next-line
  }, [speakingFilters, tab]);
  const openSpeakingEditor = async (pid) => {
    if (!pid) return setEditingSpeaking(emptySpeaking);
    try {
      const p = await ieltsApi.adminSpeakingGet(pid);
      setEditingSpeaking({ ...emptySpeaking, ...p });
    } catch (e) { toast.error(e.message); }
  };
  const saveSpeaking = async () => {
    if (!editingSpeaking) return;
    if (!editingSpeaking.prompt_id) { toast.error('prompt_id is required'); return; }
    const payload = {
      ...editingSpeaking,
      follow_up_questions: Array.isArray(editingSpeaking.follow_up_questions)
        ? editingSpeaking.follow_up_questions
        : String(editingSpeaking.follow_up_questions || '').split('\n').map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (speakingPrompts.find((p) => p.prompt_id === payload.prompt_id)) {
        await ieltsApi.adminSpeakingUpdate(payload.prompt_id, payload);
      } else {
        await ieltsApi.adminSpeakingCreate(payload);
      }
      toast.success('Speaking prompt saved');
      setEditingSpeaking(null);
      loadSpeakingPrompts();
    } catch (e) { toast.error(e.message); }
  };
  const removeSpeaking = async (pid) => {
    if (!window.confirm('Delete this speaking prompt?')) return;
    try { await ieltsApi.adminSpeakingDelete(pid); toast.success('Deleted'); loadSpeakingPrompts(); }
    catch (e) { toast.error(e.message); }
  };
  const onUploadSpeaking = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const items = Array.isArray(parsed) ? parsed : parsed.prompts;
      if (!Array.isArray(items)) throw new Error('Expected an array of prompts or {prompts: [...]}');
      const r = await ieltsApi.adminSpeakingBulk(items);
      toast.success(`Saved ${r.saved} prompts${r.errors?.length ? ` · ${r.errors.length} errors` : ''}`);
      loadSpeakingPrompts();
    } catch (e) { toast.error(e.message); }
    finally { if (speakingFileRef.current) speakingFileRef.current.value = ''; }
  };

  // ---- Save / delete handlers ----------------------------------------------
  const saveQuestion = async () => {
    try {
      const payload = { ...editing };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      if (editing.id) await ieltsApi.adminUpdate(editing.id, payload);
      else await ieltsApi.adminCreate(payload);
      toast.success('Saved'); setEditing(null); loadQuestions();
    } catch (e) { toast.error(e.message); }
  };

  const removeQuestion = async (qid) => {
    if (!window.confirm('Delete this question?')) return;
    try { await ieltsApi.adminDelete(qid); toast.success('Deleted'); loadQuestions(); }
    catch (e) { toast.error(e.message); }
  };

  const openPassageEditor = async (pid) => {
    if (!pid) return setEditingPassage(emptyPassage);
    try {
      const r = await ieltsApi.adminPassageGet(pid);
      const p = r.passage;
      // Hydrate questions from the API shape into our editor shape
      const questions = (r.questions || []).map((q) => ({
        question_number: q.question_number,
        question_type: q.question_type || 'multiple_choice_one_answer',
        instructions: q.instructions || '',
        group_id: q.group_id || '',
        question_text: q.question_text || '',
        option_a: q.option_a || '', option_b: q.option_b || '',
        option_c: q.option_c || '', option_d: q.option_d || '',
        option_e: q.option_e || '',
        correct_answer: q.correct_answer || '',
        explanation: q.explanation || '',
        estimated_time_seconds: q.estimated_time_seconds || 75,
        band_level: q.band_level || '',
      }));
      setEditingPassage({ ...emptyPassage, ...p, questions });
    } catch (e) { toast.error(e.message); }
  };

  const savePassage = async () => {
    if (!editingPassage) return;
    const payload = { ...editingPassage };
    payload.questions = (payload.questions || []).map((q) => ({
      ...q,
      question_number: Number(q.question_number) || 0,
      estimated_time_seconds: Number(q.estimated_time_seconds) || 75,
    }));
    try {
      if (passages.find((p) => p.passage_id === payload.passage_id)) {
        await ieltsApi.adminPassageUpdate(payload.passage_id, payload);
      } else {
        await ieltsApi.adminPassageCreate(payload);
      }
      toast.success(`Passage saved (${payload.questions.length} questions)`);
      setEditingPassage(null);
      loadPassages();
    } catch (e) { toast.error(e.message); }
  };

  const removePassage = async (pid) => {
    if (!window.confirm('Delete this passage AND all linked questions?')) return;
    try { await ieltsApi.adminPassageDelete(pid); toast.success('Deleted'); loadPassages(); }
    catch (e) { toast.error(e.message); }
  };

  const onUploadQuestions = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      const questions = JSON.parse(text);
      if (!Array.isArray(questions)) throw new Error('Expected an array of questions');
      const r = await ieltsApi.adminBulkUpload(questions);
      toast.success(`Uploaded ${r.inserted} (skipped ${r.skipped})`); loadQuestions();
    } catch (e) { toast.error(e.message); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  const onUploadPassages = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const items = Array.isArray(parsed) ? parsed : parsed.passages;
      if (!Array.isArray(items)) throw new Error('Expected an array of passages or {passages: [...]}');
      const r = await ieltsApi.adminPassageBulk(items);
      toast.success(`Saved ${r.saved} passages${r.errors?.length ? ` · ${r.errors.length} errors` : ''}`);
      if (r.errors?.length) console.warn('Passage upload errors', r.errors);
      loadPassages();
    } catch (e) { toast.error(e.message); }
    finally { if (passageFileRef.current) passageFileRef.current.value = ''; }
  };

  // ---- Passage editor helpers ----------------------------------------------
  const updatePassageField = (k, v) => setEditingPassage((p) => ({ ...p, [k]: v }));
  const updateQField = (idx, k, v) => setEditingPassage((p) => {
    const next = [...(p.questions || [])];
    next[idx] = { ...next[idx], [k]: v };
    return { ...p, questions: next };
  });
  const addQuestionRow = () => setEditingPassage((p) => ({
    ...p,
    questions: [...(p.questions || []), {
      question_number: (p.questions?.length || 0) + 1,
      question_type: 'true_false_not_given',
      instructions: '',
      question_text: '',
      option_a: '', option_b: '', option_c: '', option_d: '', option_e: '',
      correct_answer: '',
      explanation: '',
      estimated_time_seconds: 75,
      band_level: '',
    }],
  }));
  const removeQuestionRow = (idx) => setEditingPassage((p) => ({
    ...p,
    questions: (p.questions || []).filter((_, i) => i !== idx),
  }));

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="admin-ielts-panel">
      <Seo title="IELTS Admin · Winhouse" canonical="/admin/ielts" />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-10 w-full">
        <h1 className="text-3xl font-bold mb-6">IELTS Admin</h1>
        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === 'students') loadStudents(); }}>
          <TabsList className="mb-4">
            <TabsTrigger value="passages" data-testid="aielts-tab-passages">Reading Passages</TabsTrigger>
            <TabsTrigger value="listening" data-testid="aielts-tab-listening">Listening Sets</TabsTrigger>
            <TabsTrigger value="writing" data-testid="aielts-tab-writing">Writing Prompts</TabsTrigger>
            <TabsTrigger value="speaking" data-testid="aielts-tab-speaking">Speaking Prompts</TabsTrigger>
            <TabsTrigger value="questions" data-testid="aielts-tab-questions">Questions (legacy)</TabsTrigger>
            <TabsTrigger value="students" data-testid="aielts-tab-students">Students</TabsTrigger>
          </TabsList>

          {/* ────────────────────────────── PASSAGES ────────────────────────────── */}
          <TabsContent value="passages">
            <Card className="mb-4">
              <CardContent className="p-4 flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Module</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={passageFilters.module}
                    onChange={(e) => setPassageFilters({ ...passageFilters, module: e.target.value })}>
                    <option value="">All</option><option value="academic">Academic</option><option value="general">General</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Difficulty</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={passageFilters.difficulty}
                    onChange={(e) => setPassageFilters({ ...passageFilters, difficulty: e.target.value })}>
                    <option value="">All</option><option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                  </select>
                </div>
                <div className="ml-auto flex gap-2 flex-wrap">
                  <Button onClick={async () => {
                    if (!window.confirm('Seed reading passages from bundled file /app/backend/seed_data/ielts_reading.json?\n\nThis will INSERT new passages and REPLACE existing ones with the same passage_id. Safe to re-run.')) return;
                    try {
                      const r = await ieltsApi.adminSeedReadingFromBundle();
                      const c = r.current_db_counts || {};
                      toast.success(`Seed complete: ${r.saved} passages saved · ${c.passages || 0} total · ${c.passage_linked_questions || 0} questions in DB`);
                      if (r.errors?.length) console.warn('Seed errors:', r.errors);
                      loadPassages();
                    } catch (e) { toast.error(e.message); }
                  }} variant="secondary" className="gap-1.5" data-testid="aielts-seed-reading">
                    Seed from bundled file
                  </Button>
                  <Button onClick={() => setEditingPassage(emptyPassage)} className="gap-1.5" data-testid="aielts-new-passage">
                    <Plus className="h-3.5 w-3.5" /> New passage
                  </Button>
                  <input type="file" accept=".json" ref={passageFileRef} onChange={onUploadPassages} className="hidden" />
                  <Button variant="outline" className="gap-1.5" onClick={() => passageFileRef.current?.click()} data-testid="aielts-upload-passages">
                    <Upload className="h-3.5 w-3.5" /> Upload Passage JSON
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                {passagesLoading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
                : (
                  <table className="w-full text-sm" data-testid="aielts-passages-table">
                    <thead><tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                      <th className="px-4 py-2">Passage ID</th>
                      <th className="px-2">Title</th>
                      <th className="px-2">Module</th>
                      <th className="px-2">Part</th>
                      <th className="px-2">Difficulty</th>
                      <th className="px-2">Questions</th>
                      <th className="px-2 text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                      {passages.map((p) => (
                        <tr key={p.passage_id} className="border-b border-gray-50">
                          <td className="px-4 py-2 font-mono text-xs">{p.passage_id}</td>
                          <td className="px-2 max-w-md break-words">{p.passage_title}</td>
                          <td className="px-2">{p.module}</td>
                          <td className="px-2">{(p.passage_part || '').replace('_', ' ')}</td>
                          <td className="px-2"><Badge variant="outline" className="capitalize">{p.difficulty}</Badge></td>
                          <td className="px-2">{p.question_count}</td>
                          <td className="px-2 text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => openPassageEditor(p.passage_id)}><Edit2 className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                            <Button size="sm" variant="ghost" onClick={() => removePassage(p.passage_id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                          </td>
                        </tr>
                      ))}
                      {!passages.length && (
                        <tr><td colSpan={7} className="text-center p-10 text-gray-500">
                          <BookOpen className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                          No reading passages yet. Click "New passage" or "Upload Passage JSON" to add one.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <div className="mt-3 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <span className="font-semibold text-amber-900">Bulk upload format:</span> top-level JSON array of passage objects.
              Each passage carries its own <code>passage_id</code>, <code>passage_title</code>, <code>passage_text</code>, and a
              <code>questions</code> array. See <code>/app/scripts/ielts_passage_template.json</code> for an example.
            </div>
          </TabsContent>

          {/* ────────────────────────────── LISTENING SETS ────────────────────────────── */}
          <TabsContent value="listening">
            <Card className="mb-4">
              <CardContent className="p-4 flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Module</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={listeningFilters.module}
                    onChange={(e) => setListeningFilters({ ...listeningFilters, module: e.target.value })}>
                    <option value="">All</option><option value="academic">Academic</option><option value="general">General</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Section</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={listeningFilters.section_number}
                    onChange={(e) => setListeningFilters({ ...listeningFilters, section_number: e.target.value })}>
                    <option value="">All</option>
                    <option value="section_1">Section 1</option>
                    <option value="section_2">Section 2</option>
                    <option value="section_3">Section 3</option>
                    <option value="section_4">Section 4</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Difficulty</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={listeningFilters.difficulty}
                    onChange={(e) => setListeningFilters({ ...listeningFilters, difficulty: e.target.value })}>
                    <option value="">All</option><option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                  </select>
                </div>
                <div className="ml-auto flex gap-2 flex-wrap">
                  <Button onClick={exportTranscripts} variant="outline" className="gap-1.5" data-testid="aielts-export-transcripts">
                    <Download className="h-3.5 w-3.5" /> Export transcripts
                  </Button>
                  <input type="file" accept=".json" ref={audioUrlsFileRef} onChange={onUploadAudioUrls} className="hidden" />
                  <Button onClick={() => audioUrlsFileRef.current?.click()} variant="outline" className="gap-1.5" data-testid="aielts-upload-audio-urls">
                    <Link2 className="h-3.5 w-3.5" /> Bulk audio URLs
                  </Button>
                  <Button onClick={async () => {
                    if (!window.confirm('Seed listening sets from bundled file /app/backend/seed_data/ielts_listening.json?\n\nThis will INSERT new sets and REPLACE existing sets with the same listening_set_id. Safe to re-run.')) return;
                    try {
                      const r = await ieltsApi.adminSeedListeningFromBundle();
                      const c = r.current_db_counts || {};
                      toast.success(`Seed complete: ${r.saved} sets saved · ${c.listening_sets || 0} total sets · ${c.set_linked_questions || 0} questions in DB`);
                      if (r.errors?.length) console.warn('Seed errors:', r.errors);
                      loadListening();
                    } catch (e) { toast.error(e.message); }
                  }} variant="secondary" className="gap-1.5" data-testid="aielts-seed-listening">
                    Seed from bundled file
                  </Button>
                  <Button onClick={() => setEditingListening(emptyListeningSet)} className="gap-1.5" data-testid="aielts-new-listening">
                    <Plus className="h-3.5 w-3.5" /> New listening set
                  </Button>
                  <input type="file" accept=".json" ref={listeningFileRef} onChange={onUploadListening} className="hidden" />
                  <Button variant="outline" className="gap-1.5" onClick={() => listeningFileRef.current?.click()} data-testid="aielts-upload-listening">
                    <Upload className="h-3.5 w-3.5" /> Upload Listening JSON
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                {listeningLoading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
                : (
                  <table className="w-full text-sm" data-testid="aielts-listening-table">
                    <thead><tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                      <th className="px-4 py-2">Set ID</th>
                      <th className="px-2">Topic</th>
                      <th className="px-2">Section</th>
                      <th className="px-2">Module</th>
                      <th className="px-2">Difficulty</th>
                      <th className="px-2">Audio?</th>
                      <th className="px-2">Questions</th>
                      <th className="px-2 text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                      {listeningSets.map((s) => (
                        <tr key={s.listening_set_id} className="border-b border-gray-50">
                          <td className="px-4 py-2 font-mono text-xs">{s.listening_set_id}</td>
                          <td className="px-2 max-w-xs break-words">{s.topic}{s.subtopic ? ` — ${s.subtopic}` : ''}</td>
                          <td className="px-2">{(s.section_number || '').replace('_', ' ')}</td>
                          <td className="px-2">{s.module}</td>
                          <td className="px-2"><Badge variant="outline" className="capitalize">{s.difficulty}</Badge></td>
                          <td className="px-2">
                            <button
                              type="button"
                              onClick={() => quickSetAudioUrl(s.listening_set_id, s.audio_url)}
                              data-testid={`aielts-audio-quick-${s.listening_set_id}`}
                              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs border transition-colors ${
                                s.audio_url
                                  ? 'border-green-300 bg-green-50 text-green-800 hover:bg-green-100'
                                  : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                              }`}
                              title={s.audio_url || 'No audio URL — click to add'}
                            >
                              <Link2 className="h-3 w-3" />
                              {s.audio_url ? 'Audio ✓' : 'Set URL'}
                            </button>
                          </td>
                          <td className="px-2">{s.question_count}</td>
                          <td className="px-2 text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => openListeningEditor(s.listening_set_id)}><Edit2 className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                            <Button size="sm" variant="ghost" onClick={() => removeListening(s.listening_set_id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                          </td>
                        </tr>
                      ))}
                      {!listeningSets.length && (
                        <tr><td colSpan={8} className="text-center p-10 text-gray-500">
                          <Headphones className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                          No listening sets yet. Click "New listening set" or "Upload Listening JSON" to add one.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <div className="mt-3 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
              <p>
                <span className="font-semibold text-amber-900">Bulk upload format:</span> top-level JSON array of listening-set objects.
                Each set carries <code>listening_set_id</code>, <code>section_number</code> (section_1..4),
                <code>audio_url</code> (external Cloudinary/S3 URL), <code>transcript_text</code>,
                and a <code>questions</code> array. See <code>/app/scripts/ielts_listening_template.json</code> for an example.
              </p>
              <p>
                <span className="font-semibold text-amber-900">Audio workflow:</span>{' '}
                <span className="font-semibold">1)</span> Click <em>Export transcripts</em> to download the current bank as JSON →{' '}
                <span className="font-semibold">2)</span> Feed each <code>transcript_text</code> into an external TTS tool (ElevenLabs, Google TTS) and host the resulting MP3s on Cloudinary/S3 →{' '}
                <span className="font-semibold">3)</span> Build a mapping file{' '}
                <code>{`[{"listening_set_id":"ielts_listening_001","audio_url":"https://…mp3"}, …]`}</code> and click <em>Bulk audio URLs</em>.
                Empty <code>audio_url</code> clears the link. Single-set quick edits: click the audio badge in the row.
              </p>
            </div>
          </TabsContent>

          {/* ────────────────────────────── QUESTIONS (legacy) ────────────────────────────── */}
          <TabsContent value="questions">
            <Card className="mb-4">
              <CardContent className="p-4 flex flex-wrap items-end gap-3">
                <div><label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Section</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={filters.section}
                    onChange={(e) => setFilters({ ...filters, section: e.target.value })}>
                    <option value="">All</option>{SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select></div>
                <div><label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Module</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={filters.module}
                    onChange={(e) => setFilters({ ...filters, module: e.target.value })}>
                    <option value="">All</option><option value="academic">Academic</option><option value="general">General</option>
                  </select></div>
                <div><label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Difficulty</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={filters.difficulty}
                    onChange={(e) => setFilters({ ...filters, difficulty: e.target.value })}>
                    <option value="">All</option><option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                  </select></div>
                <div className="ml-auto flex gap-2 flex-wrap">
                  <Button onClick={() => setEditing(emptyQuestion)} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> New question</Button>
                  <input type="file" accept=".json" ref={fileRef} onChange={onUploadQuestions} className="hidden" />
                  <Button variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()}><Upload className="h-3.5 w-3.5" /> Upload JSON</Button>
                </div>
              </CardContent>
            </Card>

            <Card><CardContent className="p-0 overflow-x-auto">
              {loading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
              : (
                <table className="w-full text-sm" data-testid="aielts-questions-table">
                  <thead><tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                    <th className="px-4 py-2">Section</th><th className="px-2">Type</th><th className="px-2">Topic</th>
                    <th className="px-2">Module</th><th className="px-2">Difficulty</th>
                    <th className="px-2">Passage</th>
                    <th className="px-2">Question</th><th className="px-2 text-right">Actions</th>
                  </tr></thead>
                  <tbody>
                    {list.map((q) => (
                      <tr key={q.id} className="border-b border-gray-50">
                        <td className="px-4 py-2">{q.section}</td>
                        <td className="px-2 text-xs">{(q.question_type || '').replace(/_/g,' ')}</td>
                        <td className="px-2">{q.topic}</td>
                        <td className="px-2">{q.module}</td>
                        <td className="px-2"><Badge variant="outline" className="capitalize">{q.difficulty}</Badge></td>
                        <td className="px-2 text-xs font-mono text-gray-500">{q.passage_id || <span className="italic">—</span>}</td>
                        <td className="px-2 max-w-md break-words">{q.question_text}</td>
                        <td className="px-2 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => setEditing(q)}>Edit</Button>
                          <Button size="sm" variant="ghost" onClick={() => removeQuestion(q.id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                        </td>
                      </tr>
                    ))}
                    {!list.length && <tr><td colSpan={8} className="text-center p-10 text-gray-500">No questions yet.</td></tr>}
                  </tbody>
                </table>
              )}
            </CardContent></Card>
          </TabsContent>

          {/* ────────────────────────────── WRITING PROMPTS ────────────────────────────── */}
          <TabsContent value="writing">
            <Card className="mb-4">
              <CardContent className="p-4 flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Module</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={writingFilters.module}
                    onChange={(e) => setWritingFilters({ ...writingFilters, module: e.target.value })}>
                    <option value="">All</option><option value="academic">Academic</option><option value="general">General</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Task</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={writingFilters.task_number}
                    onChange={(e) => setWritingFilters({ ...writingFilters, task_number: e.target.value })}>
                    <option value="">All</option><option value="1">Task 1</option><option value="2">Task 2</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Type</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={writingFilters.task_type}
                    onChange={(e) => setWritingFilters({ ...writingFilters, task_type: e.target.value })}>
                    {WRITING_TASK_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Difficulty</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={writingFilters.difficulty}
                    onChange={(e) => setWritingFilters({ ...writingFilters, difficulty: e.target.value })}>
                    <option value="">All</option><option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                  </select>
                </div>
                <div className="ml-auto flex gap-2 flex-wrap">
                  <Button onClick={async () => {
                    if (!window.confirm('Seed writing prompts from bundled file /app/backend/seed_data/ielts_writing.json?\n\nThis will INSERT new prompts and REPLACE existing ones with the same prompt_id. Safe to re-run.')) return;
                    try {
                      const r = await ieltsApi.adminSeedWritingFromBundle();
                      const c = r.current_db_counts || {};
                      toast.success(`Seed complete: ${r.saved} saved · AC T1 ${c.academic_task1 || 0} · AC T2 ${c.academic_task2 || 0} · GT T1 ${c.general_task1 || 0} · GT T2 ${c.general_task2 || 0}`);
                      if (r.errors?.length) console.warn('Seed errors:', r.errors);
                      loadWritingPrompts();
                    } catch (e) { toast.error(e.message); }
                  }} variant="secondary" className="gap-1.5" data-testid="aielts-seed-writing">
                    Seed from bundled file
                  </Button>
                  <Button onClick={() => setEditingWriting(emptyWriting)} className="gap-1.5" data-testid="aielts-new-writing">
                    <Plus className="h-3.5 w-3.5" /> New prompt
                  </Button>
                  <input type="file" accept=".json" ref={writingFileRef} onChange={onUploadWriting} className="hidden" />
                  <Button variant="outline" className="gap-1.5" onClick={() => writingFileRef.current?.click()} data-testid="aielts-upload-writing">
                    <Upload className="h-3.5 w-3.5" /> Upload Writing JSON
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                {writingLoading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
                : (
                  <table className="w-full text-sm" data-testid="aielts-writing-table">
                    <thead><tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                      <th className="px-4 py-2">Prompt ID</th>
                      <th className="px-2">Module</th>
                      <th className="px-2">Task</th>
                      <th className="px-2">Type</th>
                      <th className="px-2">Difficulty</th>
                      <th className="px-2">Preview</th>
                      <th className="px-2 text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                      {writingPrompts.map((p) => (
                        <tr key={p.prompt_id} className="border-b border-gray-50">
                          <td className="px-4 py-2 font-mono text-xs">{p.prompt_id}</td>
                          <td className="px-2 capitalize">{p.module}</td>
                          <td className="px-2">{p.task_number}</td>
                          <td className="px-2 capitalize">{(p.task_type || '').replace(/_/g, ' ')}</td>
                          <td className="px-2"><Badge variant="outline" className="capitalize">{p.difficulty}</Badge></td>
                          <td className="px-2 max-w-md text-xs text-gray-600 truncate">{(p.prompt_text || '').slice(0, 140)}</td>
                          <td className="px-2 text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => openWritingEditor(p.prompt_id)}><Edit2 className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                            <Button size="sm" variant="ghost" onClick={() => removeWriting(p.prompt_id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                          </td>
                        </tr>
                      ))}
                      {!writingPrompts.length && (
                        <tr><td colSpan={7} className="text-center p-10 text-gray-500">
                          <BookOpen className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                          No writing prompts yet. Click "Seed from bundled file" to load all 80 starter prompts.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <div className="mt-3 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <span className="font-semibold text-amber-900">Bulk upload format:</span> top-level JSON array of writing prompt objects.
              Each prompt needs <code>prompt_id</code>, <code>module</code> (academic/general), <code>task_number</code> (1/2),
              <code>task_type</code>, <code>prompt_text</code>, plus optional <code>instructions</code>, <code>image_url</code>,
              <code>chart_data</code>, <code>sample_answer</code>, <code>band_level</code>, <code>difficulty</code>.
            </div>
          </TabsContent>

          {/* ────────────────────────────── SPEAKING PROMPTS ────────────────────────────── */}
          <TabsContent value="speaking">
            <Card className="mb-4">
              <CardContent className="p-4 flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Part</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={speakingFilters.speaking_part}
                    onChange={(e) => setSpeakingFilters({ ...speakingFilters, speaking_part: e.target.value })}>
                    <option value="">All</option><option value="part_1">Part 1</option><option value="part_2">Part 2</option><option value="part_3">Part 3</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Topic</label>
                  <input className="border border-gray-200 rounded-lg p-2 bg-white text-sm w-36" value={speakingFilters.topic}
                    placeholder="e.g. hometown"
                    onChange={(e) => setSpeakingFilters({ ...speakingFilters, topic: e.target.value })} />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Difficulty</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={speakingFilters.difficulty}
                    onChange={(e) => setSpeakingFilters({ ...speakingFilters, difficulty: e.target.value })}>
                    <option value="">All</option><option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-gray-500 block mb-1">Status</label>
                  <select className="border border-gray-200 rounded-lg p-2 bg-white text-sm" value={speakingFilters.status}
                    onChange={(e) => setSpeakingFilters({ ...speakingFilters, status: e.target.value })}>
                    <option value="">All</option><option value="published">published</option><option value="draft">draft</option>
                  </select>
                </div>
                <div className="ml-auto flex gap-2 flex-wrap">
                  <Button onClick={async () => {
                    if (!window.confirm('Seed speaking prompts from bundled file /app/backend/seed_data/ielts_speaking.json?\n\nThis will INSERT new prompts and REPLACE existing ones with the same prompt_id. Safe to re-run.')) return;
                    try {
                      const r = await ieltsApi.adminSeedSpeakingFromBundle();
                      const c = r.current_db_counts || {};
                      toast.success(`Seed complete: ${r.saved} saved · P1 ${c.part_1 || 0} · P2 ${c.part_2 || 0} · P3 ${c.part_3 || 0}`);
                      if (r.errors?.length) console.warn('Seed errors:', r.errors);
                      loadSpeakingPrompts();
                    } catch (e) { toast.error(e.message); }
                  }} variant="secondary" className="gap-1.5" data-testid="aielts-seed-speaking">
                    Seed from bundled file
                  </Button>
                  <Button onClick={() => setEditingSpeaking(emptySpeaking)} className="gap-1.5" data-testid="aielts-new-speaking">
                    <Plus className="h-3.5 w-3.5" /> New prompt
                  </Button>
                  <input type="file" accept=".json" ref={speakingFileRef} onChange={onUploadSpeaking} className="hidden" />
                  <Button variant="outline" className="gap-1.5" onClick={() => speakingFileRef.current?.click()} data-testid="aielts-upload-speaking">
                    <Upload className="h-3.5 w-3.5" /> Upload Speaking JSON
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                {speakingLoading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
                : (
                  <table className="w-full text-sm" data-testid="aielts-speaking-table">
                    <thead><tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                      <th className="px-4 py-2">Prompt ID</th>
                      <th className="px-2">Part</th>
                      <th className="px-2">Topic</th>
                      <th className="px-2">Subtopic</th>
                      <th className="px-2">Difficulty</th>
                      <th className="px-2">Q&apos;s</th>
                      <th className="px-2 text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                      {speakingPrompts.map((p) => (
                        <tr key={p.prompt_id} className="border-b border-gray-50">
                          <td className="px-4 py-2 font-mono text-xs">{p.prompt_id}</td>
                          <td className="px-2">{p.speaking_part}</td>
                          <td className="px-2 capitalize">{(p.topic || '').replace(/_/g, ' ')}</td>
                          <td className="px-2 max-w-xs text-xs text-gray-600 truncate">{p.subtopic}</td>
                          <td className="px-2"><Badge variant="outline" className="capitalize">{p.difficulty}</Badge></td>
                          <td className="px-2 text-center">{(p.follow_up_questions || []).length}</td>
                          <td className="px-2 text-right whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => openSpeakingEditor(p.prompt_id)}><Edit2 className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                            <Button size="sm" variant="ghost" onClick={() => removeSpeaking(p.prompt_id)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
                          </td>
                        </tr>
                      ))}
                      {!speakingPrompts.length && (
                        <tr><td colSpan={7} className="text-center p-10 text-gray-500">
                          <Headphones className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                          No speaking prompts yet. Click "Seed from bundled file" to load all 120 starter prompts.
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            <div className="mt-3 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <span className="font-semibold text-amber-900">Bulk upload format:</span> top-level JSON array of speaking prompt objects.
              Each needs <code>prompt_id</code>, <code>speaking_part</code> (part_1/part_2/part_3), <code>topic</code>,
              <code>prompt_text</code> (or <code>cue_card_text</code> for Part 2), plus optional <code>subtopic</code>,
              <code>follow_up_questions</code>, <code>difficulty</code>, <code>band_level</code>.
            </div>
          </TabsContent>

          {/* ────────────────────────────── STUDENTS ────────────────────────────── */}
          <TabsContent value="students">
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                  <th className="px-4 py-2">Email</th><th className="px-2">Name</th><th className="px-2">Attempts</th><th className="px-2">Last login</th>
                </tr></thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.user_id} className="border-b border-gray-50">
                      <td className="px-4 py-2">{s.email}</td>
                      <td className="px-2">{s.name}</td>
                      <td className="px-2">{s.attempts}</td>
                      <td className="px-2 text-xs text-gray-500">{s.last_login_at}</td>
                    </tr>
                  ))}
                  {!students.length && <tr><td colSpan={4} className="text-center p-10 text-gray-500">No students yet.</td></tr>}
                </tbody>
              </table>
            </CardContent></Card>
          </TabsContent>
        </Tabs>

        {/* ────────────────────────────── Question editor modal ────────────────────────────── */}
        {editing && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl w-full max-w-3xl my-8">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h2 className="font-bold text-lg">{editing.id ? 'Edit question' : 'New question'}</h2>
                <Button variant="ghost" onClick={() => setEditing(null)}>Close</Button>
              </div>
              <div className="px-6 py-4 grid sm:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
                {['section','module','passage_id','topic','subtopic','difficulty','question_type','correct_answer','status','source_type','estimated_time_seconds','band_level'].map((k) => (
                  <label key={k} className="text-sm">
                    <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">{k}</span>
                    {(k === 'section') ?
                      <select className="border border-gray-200 rounded-lg p-2 w-full" value={editing[k] ?? ''} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}>
                        {SECTIONS.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    : (k === 'module') ?
                      <select className="border border-gray-200 rounded-lg p-2 w-full" value={editing[k] ?? ''} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}>
                        <option value="academic">academic</option><option value="general">general</option>
                      </select>
                    : (k === 'difficulty') ?
                      <select className="border border-gray-200 rounded-lg p-2 w-full" value={editing[k] ?? ''} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}>
                        <option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                      </select>
                    : (k === 'question_type') ?
                      <select className="border border-gray-200 rounded-lg p-2 w-full" value={editing[k] ?? ''} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}>
                        {QTYPES.map((q) => <option key={q}>{q}</option>)}
                      </select>
                    :
                      <input className="border border-gray-200 rounded-lg p-2 w-full" value={editing[k] ?? ''} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} />
                    }
                  </label>
                ))}
                {['instructions','passage_text','transcript_text','audio_url','image_url','question_text','option_a','option_b','option_c','option_d','option_e','explanation'].map((k) => (
                  <label key={k} className="text-sm sm:col-span-2">
                    <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">{k}</span>
                    <textarea className="border border-gray-200 rounded-lg p-2 w-full" rows={k.endsWith('_text') || k === 'instructions' ? 4 : 2}
                      value={editing[k] ?? ''} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} />
                  </label>
                ))}
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={saveQuestion}>Save</Button>
              </div>
            </div>
          </div>
        )}

        {/* ────────────────────────────── Passage editor modal ────────────────────────────── */}
        {editingPassage && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl w-full max-w-5xl my-8">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h2 className="font-bold text-lg flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  {passages.find((p) => p.passage_id === editingPassage.passage_id) ? 'Edit passage' : 'New passage'}
                </h2>
                <Button variant="ghost" onClick={() => setEditingPassage(null)}>Close</Button>
              </div>
              <div className="px-6 py-4 grid sm:grid-cols-2 gap-3 max-h-[75vh] overflow-y-auto">
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Passage ID*</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full font-mono text-xs"
                    placeholder="ielts_reading_ac_001"
                    value={editingPassage.passage_id}
                    onChange={(e) => updatePassageField('passage_id', e.target.value)}
                    data-testid="aielts-passage-id" />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Title*</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full"
                    value={editingPassage.passage_title}
                    onChange={(e) => updatePassageField('passage_title', e.target.value)}
                    data-testid="aielts-passage-title" />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Module</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full"
                    value={editingPassage.module}
                    onChange={(e) => updatePassageField('module', e.target.value)}>
                    <option value="academic">academic</option>
                    <option value="general">general</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Passage Part</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full"
                    value={editingPassage.passage_part}
                    onChange={(e) => updatePassageField('passage_part', e.target.value)}>
                    <option value="part_1">part_1</option>
                    <option value="part_2">part_2</option>
                    <option value="part_3">part_3</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Difficulty</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full"
                    value={editingPassage.difficulty}
                    onChange={(e) => updatePassageField('difficulty', e.target.value)}>
                    <option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Topic</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full"
                    value={editingPassage.topic}
                    onChange={(e) => updatePassageField('topic', e.target.value)} />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Passage Text*</span>
                  <textarea className="border border-gray-200 rounded-lg p-2 w-full text-sm"
                    rows={12} placeholder="Paste the full reading passage here…"
                    value={editingPassage.passage_text}
                    onChange={(e) => updatePassageField('passage_text', e.target.value)}
                    data-testid="aielts-passage-text" />
                </label>

                <div className="sm:col-span-2 border-t pt-4 mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm">Linked Questions ({editingPassage.questions?.length || 0})</h3>
                    <Button size="sm" variant="outline" onClick={addQuestionRow} className="gap-1.5">
                      <Plus className="h-3.5 w-3.5" /> Add question
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {(editingPassage.questions || []).map((q, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50/60">
                        <div className="grid sm:grid-cols-4 gap-2 mb-2">
                          <label className="text-xs">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">#</span>
                            <input type="number" className="border border-gray-200 rounded p-1.5 w-full text-sm"
                              value={q.question_number} onChange={(e) => updateQField(idx, 'question_number', e.target.value)} />
                          </label>
                          <label className="text-xs sm:col-span-2">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Type</span>
                            <select className="border border-gray-200 rounded p-1.5 w-full text-sm"
                              value={q.question_type} onChange={(e) => updateQField(idx, 'question_type', e.target.value)}>
                              {QTYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                          </label>
                          <label className="text-xs">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Correct answer</span>
                            <input className="border border-gray-200 rounded p-1.5 w-full text-sm"
                              value={q.correct_answer || ''} onChange={(e) => updateQField(idx, 'correct_answer', e.target.value)} />
                          </label>
                        </div>
                        <label className="text-xs block mb-2">
                          <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Instructions</span>
                          <textarea className="border border-gray-200 rounded p-1.5 w-full text-sm" rows={2}
                            value={q.instructions} onChange={(e) => updateQField(idx, 'instructions', e.target.value)} />
                        </label>
                        <label className="text-xs block mb-2">
                          <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Question text</span>
                          <textarea className="border border-gray-200 rounded p-1.5 w-full text-sm" rows={2}
                            value={q.question_text} onChange={(e) => updateQField(idx, 'question_text', e.target.value)} />
                        </label>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {['option_a','option_b','option_c','option_d','option_e'].map((k) => (
                            <label key={k} className="text-xs">
                              <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">{k}</span>
                              <input className="border border-gray-200 rounded p-1.5 w-full text-sm"
                                value={q[k] || ''} onChange={(e) => updateQField(idx, k, e.target.value)} />
                            </label>
                          ))}
                          <label className="text-xs sm:col-span-2">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Explanation</span>
                            <textarea className="border border-gray-200 rounded p-1.5 w-full text-sm" rows={2}
                              value={q.explanation} onChange={(e) => updateQField(idx, 'explanation', e.target.value)} />
                          </label>
                        </div>
                        <div className="flex justify-end mt-2">
                          <Button size="sm" variant="ghost" onClick={() => removeQuestionRow(idx)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove question
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingPassage(null)}>Cancel</Button>
                <Button onClick={savePassage} data-testid="aielts-passage-save">Save Passage</Button>
              </div>
            </div>
          </div>
        )}

        {/* ────────────────────────────── Listening Set editor modal ────────────────────────────── */}
        {editingListening && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl w-full max-w-5xl my-8">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h2 className="font-bold text-lg flex items-center gap-2">
                  <Headphones className="h-4 w-4" />
                  {listeningSets.find((s) => s.listening_set_id === editingListening.listening_set_id) ? 'Edit listening set' : 'New listening set'}
                </h2>
                <Button variant="ghost" onClick={() => setEditingListening(null)}>Close</Button>
              </div>
              <div className="px-6 py-4 grid sm:grid-cols-2 gap-3 max-h-[75vh] overflow-y-auto">
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Listening Set ID*</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full font-mono text-xs"
                    placeholder="ielts_listening_001"
                    value={editingListening.listening_set_id}
                    onChange={(e) => updateListeningField('listening_set_id', e.target.value)}
                    data-testid="aielts-listening-id" />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Topic*</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full"
                    placeholder="e.g. Accommodation"
                    value={editingListening.topic}
                    onChange={(e) => updateListeningField('topic', e.target.value)} />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Subtopic</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full"
                    placeholder="e.g. Student Housing Enquiry"
                    value={editingListening.subtopic || ''}
                    onChange={(e) => updateListeningField('subtopic', e.target.value)} />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Module</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full"
                    value={editingListening.module}
                    onChange={(e) => updateListeningField('module', e.target.value)}>
                    <option value="academic">academic</option>
                    <option value="general">general</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Section Number</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full"
                    value={editingListening.section_number}
                    onChange={(e) => updateListeningField('section_number', e.target.value)}>
                    <option value="section_1">section_1</option>
                    <option value="section_2">section_2</option>
                    <option value="section_3">section_3</option>
                    <option value="section_4">section_4</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Difficulty</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full"
                    value={editingListening.difficulty}
                    onChange={(e) => updateListeningField('difficulty', e.target.value)}>
                    <option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                  </select>
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">
                    Audio URL <span className="normal-case text-gray-400">(external — Cloudinary / S3 / Google Drive direct link)</span>
                  </span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full font-mono text-xs"
                    placeholder="https://… .mp3"
                    value={editingListening.audio_url || ''}
                    onChange={(e) => updateListeningField('audio_url', e.target.value)}
                    data-testid="aielts-listening-audio-url" />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Map / Diagram URL <span className="normal-case text-gray-400">(optional)</span></span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full font-mono text-xs"
                    placeholder="https://…"
                    value={editingListening.image_url || ''}
                    onChange={(e) => updateListeningField('image_url', e.target.value)} />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Section Instructions</span>
                  <textarea className="border border-gray-200 rounded-lg p-2 w-full" rows={2}
                    placeholder="e.g. Complete the notes below. Write ONE WORD AND/OR A NUMBER for each answer."
                    value={editingListening.instructions || ''}
                    onChange={(e) => updateListeningField('instructions', e.target.value)} />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">
                    Transcript Text <span className="normal-case text-gray-400">(required if no audio URL)</span>
                  </span>
                  <textarea className="border border-gray-200 rounded-lg p-2 w-full text-sm"
                    rows={10}
                    placeholder="Full transcript of the listening section…"
                    value={editingListening.transcript_text || ''}
                    onChange={(e) => updateListeningField('transcript_text', e.target.value)}
                    data-testid="aielts-listening-transcript" />
                </label>

                <div className="sm:col-span-2 border-t pt-4 mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm">Linked Questions ({editingListening.questions?.length || 0})</h3>
                    <Button size="sm" variant="outline" onClick={addListeningQuestionRow} className="gap-1.5">
                      <Plus className="h-3.5 w-3.5" /> Add question
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {(editingListening.questions || []).map((q, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50/60">
                        <div className="grid sm:grid-cols-4 gap-2 mb-2">
                          <label className="text-xs">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">#</span>
                            <input type="number" className="border border-gray-200 rounded p-1.5 w-full text-sm"
                              value={q.question_number} onChange={(e) => updateLQField(idx, 'question_number', e.target.value)} />
                          </label>
                          <label className="text-xs sm:col-span-2">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Type</span>
                            <select className="border border-gray-200 rounded p-1.5 w-full text-sm"
                              value={q.question_type} onChange={(e) => updateLQField(idx, 'question_type', e.target.value)}>
                              {LISTENING_QTYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                          </label>
                          <label className="text-xs">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Correct answer</span>
                            <input className="border border-gray-200 rounded p-1.5 w-full text-sm"
                              value={q.correct_answer || ''} onChange={(e) => updateLQField(idx, 'correct_answer', e.target.value)} />
                          </label>
                        </div>
                        <label className="text-xs block mb-2">
                          <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Group instructions</span>
                          <textarea className="border border-gray-200 rounded p-1.5 w-full text-sm" rows={2}
                            value={q.instructions} onChange={(e) => updateLQField(idx, 'instructions', e.target.value)} />
                        </label>
                        <label className="text-xs block mb-2">
                          <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Question text</span>
                          <textarea className="border border-gray-200 rounded p-1.5 w-full text-sm" rows={2}
                            value={q.question_text} onChange={(e) => updateLQField(idx, 'question_text', e.target.value)} />
                        </label>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {['option_a','option_b','option_c','option_d','option_e'].map((k) => (
                            <label key={k} className="text-xs">
                              <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">{k}</span>
                              <input className="border border-gray-200 rounded p-1.5 w-full text-sm"
                                value={q[k] || ''} onChange={(e) => updateLQField(idx, k, e.target.value)} />
                            </label>
                          ))}
                          <label className="text-xs sm:col-span-2">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Explanation</span>
                            <textarea className="border border-gray-200 rounded p-1.5 w-full text-sm" rows={2}
                              value={q.explanation} onChange={(e) => updateLQField(idx, 'explanation', e.target.value)} />
                          </label>
                        </div>
                        <div className="flex justify-end mt-2">
                          <Button size="sm" variant="ghost" onClick={() => removeListeningQuestionRow(idx)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove question
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingListening(null)}>Cancel</Button>
                <Button onClick={saveListening} data-testid="aielts-listening-save">Save Listening Set</Button>
              </div>
            </div>
          </div>
        )}

        {/* ────────────────────────────── Writing prompt editor modal ────────────────────────────── */}
        {editingWriting && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl w-full max-w-3xl my-8">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h2 className="font-bold text-lg">{writingPrompts.find((p) => p.prompt_id === editingWriting.prompt_id) ? 'Edit writing prompt' : 'New writing prompt'}</h2>
                <Button variant="ghost" onClick={() => setEditingWriting(null)}>Close</Button>
              </div>
              <div className="p-6 max-h-[70vh] overflow-y-auto grid sm:grid-cols-2 gap-3">
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Prompt ID <span className="text-red-600">*</span></span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full text-sm font-mono"
                    value={editingWriting.prompt_id}
                    onChange={(e) => setEditingWriting({ ...editingWriting, prompt_id: e.target.value })}
                    data-testid="aielts-writing-id" />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Module</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full text-sm bg-white" value={editingWriting.module}
                    onChange={(e) => setEditingWriting({ ...editingWriting, module: e.target.value })}>
                    <option value="academic">Academic</option><option value="general">General</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Task number</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full text-sm bg-white" value={editingWriting.task_number}
                    onChange={(e) => setEditingWriting({ ...editingWriting, task_number: Number(e.target.value) })}>
                    <option value={1}>Task 1</option><option value={2}>Task 2</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Task type</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full text-sm bg-white" value={editingWriting.task_type}
                    onChange={(e) => setEditingWriting({ ...editingWriting, task_type: e.target.value })}>
                    {WRITING_TASK_TYPES.filter((t) => t.v).map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Difficulty</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full text-sm bg-white" value={editingWriting.difficulty}
                    onChange={(e) => setEditingWriting({ ...editingWriting, difficulty: e.target.value })}>
                    <option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                  </select>
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Instructions</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full text-sm"
                    placeholder="e.g. You should spend about 20 minutes on this task. Write at least 150 words."
                    value={editingWriting.instructions || ''}
                    onChange={(e) => setEditingWriting({ ...editingWriting, instructions: e.target.value })} />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Prompt text <span className="text-red-600">*</span></span>
                  <textarea className="border border-gray-200 rounded-lg p-2 w-full text-sm font-serif"
                    rows={6} value={editingWriting.prompt_text || ''}
                    onChange={(e) => setEditingWriting({ ...editingWriting, prompt_text: e.target.value })}
                    data-testid="aielts-writing-prompt-text" />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Image URL (optional · for Task 1 charts/maps)</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full text-sm"
                    placeholder="https://res.cloudinary.com/.../chart.png  OR  /ielts/writing/line_graph.svg"
                    value={editingWriting.image_url || ''}
                    onChange={(e) => setEditingWriting({ ...editingWriting, image_url: e.target.value })}
                    data-testid="aielts-writing-image-url" />
                  {editingWriting.image_url ? (
                    <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-2" data-testid="aielts-writing-image-preview">
                      <img src={editingWriting.image_url} alt="preview"
                        className="max-h-40 mx-auto"
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'block'; }} />
                      <div className="text-xs text-red-600 text-center" style={{ display: 'none' }}>Image failed to load — check URL</div>
                    </div>
                  ) : null}
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Task title (e.g. "Internet banking adoption in the UK")</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full text-sm"
                    value={editingWriting.task_title || ''}
                    onChange={(e) => setEditingWriting({ ...editingWriting, task_title: e.target.value })}
                    data-testid="aielts-writing-task-title" />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Chart label (display)</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full text-sm bg-white"
                    value={editingWriting.chart_type || ''}
                    onChange={(e) => setEditingWriting({ ...editingWriting, chart_type: e.target.value })}
                    data-testid="aielts-writing-chart-type">
                    <option value="">— None / text-only —</option>
                    <option value="Line graph">Line graph</option>
                    <option value="Bar chart">Bar chart</option>
                    <option value="Pie chart">Pie chart</option>
                    <option value="Table">Table</option>
                    <option value="Process diagram">Process diagram</option>
                    <option value="Map">Map</option>
                    <option value="Mixed visual">Mixed visual</option>
                  </select>
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Sample answer (optional · not shown to students)</span>
                  <textarea className="border border-gray-200 rounded-lg p-2 w-full text-sm font-serif"
                    rows={4} value={editingWriting.sample_answer || ''}
                    onChange={(e) => setEditingWriting({ ...editingWriting, sample_answer: e.target.value })} />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Band level (label)</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full text-sm"
                    value={editingWriting.band_level || ''}
                    onChange={(e) => setEditingWriting({ ...editingWriting, band_level: e.target.value })} />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Status</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full text-sm bg-white" value={editingWriting.status}
                    onChange={(e) => setEditingWriting({ ...editingWriting, status: e.target.value })}>
                    <option value="published">published</option><option value="draft">draft</option>
                  </select>
                </label>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingWriting(null)}>Cancel</Button>
                <Button onClick={saveWriting} data-testid="aielts-writing-save">Save prompt</Button>
              </div>
            </div>
          </div>
        )}

        {/* ────────────────────────────── Speaking prompt editor modal ────────────────────────────── */}
        {editingSpeaking && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl w-full max-w-3xl my-8">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h2 className="font-bold text-lg">{speakingPrompts.find((p) => p.prompt_id === editingSpeaking.prompt_id) ? 'Edit speaking prompt' : 'New speaking prompt'}</h2>
                <Button variant="ghost" onClick={() => setEditingSpeaking(null)}>Close</Button>
              </div>
              <div className="p-6 max-h-[70vh] overflow-y-auto grid sm:grid-cols-2 gap-3">
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Prompt ID <span className="text-red-600">*</span></span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full text-sm font-mono"
                    value={editingSpeaking.prompt_id}
                    onChange={(e) => setEditingSpeaking({ ...editingSpeaking, prompt_id: e.target.value })}
                    data-testid="aielts-speaking-id" />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Speaking part</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full text-sm bg-white" value={editingSpeaking.speaking_part}
                    onChange={(e) => setEditingSpeaking({ ...editingSpeaking, speaking_part: e.target.value })}>
                    <option value="part_1">Part 1</option><option value="part_2">Part 2</option><option value="part_3">Part 3</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Module</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full text-sm bg-white" value={editingSpeaking.module}
                    onChange={(e) => setEditingSpeaking({ ...editingSpeaking, module: e.target.value })}>
                    <option value="shared">Shared (both)</option><option value="academic">Academic</option><option value="general">General</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Topic</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full text-sm"
                    placeholder="e.g. hometown, work_study, technology"
                    value={editingSpeaking.topic || ''}
                    onChange={(e) => setEditingSpeaking({ ...editingSpeaking, topic: e.target.value })} />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Subtopic (Part 2 = card title)</span>
                  <input className="border border-gray-200 rounded-lg p-2 w-full text-sm"
                    value={editingSpeaking.subtopic || ''}
                    onChange={(e) => setEditingSpeaking({ ...editingSpeaking, subtopic: e.target.value })} />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Prompt text (Part 1/3 main question or Part 2 title) {editingSpeaking.speaking_part !== 'part_2' && <span className="text-red-600">*</span>}</span>
                  <textarea className="border border-gray-200 rounded-lg p-2 w-full text-sm font-serif"
                    rows={4} value={editingSpeaking.prompt_text || ''}
                    onChange={(e) => setEditingSpeaking({ ...editingSpeaking, prompt_text: e.target.value })}
                    data-testid="aielts-speaking-prompt-text" />
                </label>
                {editingSpeaking.speaking_part === 'part_2' && (
                  <label className="text-sm sm:col-span-2">
                    <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Cue card text (Part 2) <span className="text-red-600">*</span></span>
                    <textarea className="border border-gray-200 rounded-lg p-2 w-full text-sm font-serif"
                      rows={6} value={editingSpeaking.cue_card_text || ''}
                      placeholder={'Describe a person you admire. You should say:\n• who they are\n• how you know them\n• what they do\nand explain why you admire them.'}
                      onChange={(e) => setEditingSpeaking({ ...editingSpeaking, cue_card_text: e.target.value })}
                      data-testid="aielts-speaking-cue-card" />
                  </label>
                )}
                <label className="text-sm sm:col-span-2">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Follow-up / nested questions (one per line)</span>
                  <textarea className="border border-gray-200 rounded-lg p-2 w-full text-sm"
                    rows={4}
                    value={Array.isArray(editingSpeaking.follow_up_questions) ? editingSpeaking.follow_up_questions.join('\n') : (editingSpeaking.follow_up_questions || '')}
                    onChange={(e) => setEditingSpeaking({ ...editingSpeaking, follow_up_questions: e.target.value.split('\n').filter((s) => s.trim()) })}
                    data-testid="aielts-speaking-followups" />
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Difficulty</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full text-sm bg-white" value={editingSpeaking.difficulty}
                    onChange={(e) => setEditingSpeaking({ ...editingSpeaking, difficulty: e.target.value })}>
                    <option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Status</span>
                  <select className="border border-gray-200 rounded-lg p-2 w-full text-sm bg-white" value={editingSpeaking.status}
                    onChange={(e) => setEditingSpeaking({ ...editingSpeaking, status: e.target.value })}>
                    <option value="published">published</option><option value="draft">draft</option>
                  </select>
                </label>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingSpeaking(null)}>Cancel</Button>
                <Button onClick={saveSpeaking} data-testid="aielts-speaking-save">Save prompt</Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
export default AdminIeltsPanel;
